import { Flex, Text } from '@huspy/briks-web';
import { VStack } from '@huspy/styled-system/jsx';
import { Image, Skeleton } from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import { useTranslation } from 'react-i18next';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { AMPLITUDE_FEATURE_FLAGS } from '@modules/core/api/types';
import useGetCreditsPriceList from '@modules/core/hooks/queries/useGetCreditsPriceList';
import { CREDITS_CHARGE_ACTION_TYPES } from '@modules/core/api/const';

const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.creditUsage';

const CreditCard = ({
  title,
  amount = 0,
  description,
  isLoading,
}: {
  title: string;
  amount?: number;
  description: string;
  isLoading: boolean
}) => {
  const { t } = useTranslation();
  const translation = `${baseTranslation}.card.${amount > 0 ? 'earned' : 'used'}`;
  const subtitle = t(translation, { num: Math.abs(amount) });
  return (
    <VStack bg='blue.100' alignItems='start' p='4' borderRadius='2' w='200px'>
      <Text size='md'>{title}</Text>
      { isLoading
        ? (<Skeleton w='100%' height='50px' radius='12px' data-testid='credit-amount-skeleton' />)
        : (
          <Flex>
            <Image src={ `${imagesPath}/credits.png` } w={ 26 } h={ 26 } />
            <Text size='2xl' ml='1'>
              {subtitle}
            </Text>
          </Flex>
        )}
      <Text size='sm' color='neutral.400'>
        {description}
      </Text>
    </VStack>
  );
};

export const CreditUsage = () => {
  const { t } = useTranslation();
  const { featureFlags } = useFeatureFlags();
  const CLIENT_HUB_CREDITS_MODULE = featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_CREDITS_MODULE];
  const { isLoading, getActionPrice } = useGetCreditsPriceList(CLIENT_HUB_CREDITS_MODULE);

  if (!CLIENT_HUB_CREDITS_MODULE) {
    return <></>;
  }

  return (
    <Flex justify='space-between' align='center' gap='4' data-testid='credit-usage'>
      <VStack alignItems='start' w='50%'>
        <Text size='lg'>{t(`${baseTranslation}.title`)}</Text>
        <Text size='md' color='neutral.400'>
          {t(`${baseTranslation}.description`)}
        </Text>
      </VStack>
      <Flex gap='4'>
        <CreditCard
          title={ t(`${baseTranslation}.card.sendThroughHuspy`) }
          amount={ getActionPrice(CREDITS_CHARGE_ACTION_TYPES.firstBankApplicationToHuspySubmit) }
          description={ t(`${baseTranslation}.card.huspyDescription`, { num: 2 }) }
          isLoading={ isLoading }
        />
        <CreditCard
          title={ t(`${baseTranslation}.card.sendThroughBank`) }
          amount={ getActionPrice(CREDITS_CHARGE_ACTION_TYPES.firstBankApplicationDirectToBankSubmit) }
          description={ t(`${baseTranslation}.card.bankDescription`, { num: 3 }) }
          isLoading={ isLoading }
        />
      </Flex>
    </Flex>
  );
};
